import { useCallback, useEffect, useRef, type RefObject } from 'react';

export const activeEdgeScrollStep = 3;
export const activeEdgeScrollThreshold = 30;

interface PointerPosition {
  clientX: number;
  clientY: number;
}

export function useBoardEdgeAutoScroll(viewportRef: RefObject<HTMLElement | null>) {
  const animationFrameRef = useRef<number | null>(null);
  const pointerRef = useRef<PointerPosition | null>(null);

  const stop = useCallback(() => {
    pointerRef.current = null;

    if (animationFrameRef.current != null) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  }, []);

  const tick = useCallback(() => {
    animationFrameRef.current = null;

    const viewport = viewportRef.current;
    const pointer = pointerRef.current;

    if (!viewport || !pointer) {
      return;
    }

    const rect = viewport.getBoundingClientRect();
    let scrolled = false;

    const nearRight = pointer.clientX > rect.right - activeEdgeScrollThreshold;
    const nearBottom = pointer.clientY > rect.bottom - activeEdgeScrollThreshold;

    if (nearRight) {
      const previousScrollLeft = viewport.scrollLeft;
      viewport.scrollLeft += activeEdgeScrollStep;
      scrolled = viewport.scrollLeft !== previousScrollLeft;
    }

    if (nearBottom) {
      const previousScrollTop = viewport.scrollTop;
      viewport.scrollTop += activeEdgeScrollStep;
      scrolled = viewport.scrollTop !== previousScrollTop;
    }

    if ((nearRight || nearBottom) && !scrolled) {
      viewport.dispatchEvent(new Event('scroll'));
    }

    if (pointerRef.current && (nearRight || nearBottom)) {
      animationFrameRef.current = requestAnimationFrame(tick);
    }
  }, [viewportRef]);

  const update = useCallback(
    (pointer: PointerPosition) => {
      pointerRef.current = pointer;

      if (animationFrameRef.current == null) {
        animationFrameRef.current = requestAnimationFrame(tick);
      }
    },
    [tick]
  );

  useEffect(() => stop, [stop]);

  return { stop, update };
}
